import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

type Size = 'sm' | 'md' | 'lg';

const SIZES: Record<Size, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: Size;
}

/**
 * Built on the native `<dialog>` element.
 *
 * `showModal()` gives focus trapping, Escape handling and an inert background for free — all
 * things a hand-rolled overlay gets subtly wrong, and all things a POS operator on a keyboard
 * will notice.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Each dialog names itself: with several mounted on one page, a shared id would announce the first one's title for all of them.
  const titleId = useId();

  // Whatever had focus when the dialog opened gets it back when the dialog goes, so a keyboard user carries on where they were.
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      opener.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    }
    if (!isOpen && dialog.open) {
      dialog.close();
      if (opener.current?.isConnected) opener.current.focus();
    }
  }, [isOpen]);

  // A dialog rendered only while it is needed leaves the page still open, without its close steps, so nothing hands focus back: do it once the element is really gone. Never close it from here — StrictMode's rehearsal unmount would close a dialog the user is using, and the close event would tell the screen it was dismissed.
  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog && !dialog.isConnected && dialog.open && opener.current?.isConnected) {
        opener.current.focus();
      }
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      // Escape fires `cancel`; routing it through onClose keeps React state in step with the DOM.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      // A click on the backdrop lands on the dialog element itself, not on its contents.
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
      aria-labelledby={titleId}
      className={cn(
        'border-line m-auto w-[calc(100%-2rem)] rounded-xl border bg-white p-0 shadow-xl',
        'backdrop:bg-black/40 open:animate-none',
        SIZES[size],
      )}
    >
      <div className="border-line flex items-start justify-between gap-4 border-b px-5 py-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-ink text-base font-semibold">
            {title}
          </h2>
          {description ? <p className="text-muted mt-0.5 text-sm">{description}</p> : null}
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="text-muted hover:text-ink -me-1 shrink-0"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>

      {children ? <div className="px-5 py-4">{children}</div> : null}

      {footer ? (
        <div className="border-line bg-canvas/60 flex flex-wrap justify-end gap-2 border-t px-5 py-3">
          {footer}
        </div>
      ) : null}
    </dialog>
  );
}
